import * as React from 'react';
import { useCallback, useState } from 'react';
import { Card } from '@/components/ui/card';
import { FilesUploadButton } from '@/components/files-upload-button';
import { DocumentGrid } from '@/components/document-grid';
import { useTenant, useClientPermissions } from '@/hooks';
import {
  useDeleteInternalProjectResourceMutation,
  useGetCurrentUserQuery,
  useGetInternalProjectResourcesQuery,
  useUploadInternalProjectResourceMutation,
  useUploadExternalProjectResourceMutation,
  useDeleteExternalProjectResourceMutation,
} from '@/state';
import type {
  ApiErrorResponse,
  DocumentItem,
  ProjectResourcesProps,
} from '@/types';
import { ConfirmDialog } from '@/components/confrim-dialog';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { normalizeProjectDocs } from '@/lib';
import NoData from '@/components/no-data';
import { useSearchParams } from 'react-router-dom';

const ProjectResources: React.FC<ProjectResourcesProps> = ({ project }) => {
  const { tenant } = useTenant();
  const { isStudent, isCompany } = useClientPermissions();
  const [searchParams] = useSearchParams();
  const { data: currentUser } = useGetCurrentUserQuery({ tenant: tenant! });
  const projectType = React.useMemo(() => searchParams.get('type') ?? 'INTERNAL', [searchParams]);
  const isMentor = React.useMemo(() => {
    return (
      currentUser?.email &&
      project?.project_mentor &&
      currentUser.email.toLowerCase() === project.project_mentor.toLowerCase()
    );
  }, [currentUser?.email, project?.project_mentor]);

  const canManage = !!((!isStudent || isMentor) || (isCompany && projectType === "EXTERNAL"));

  // ConfirmDialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  // Always fetch live data so RTK Query cache invalidation triggers re-renders
  const { data, isLoading, isFetching, error, refetch } =
    useGetInternalProjectResourcesQuery(
      {
        tenant: tenant!,
        projectId: project?.id,
      },
      {
        skip: !tenant || !project?.id || projectType === 'EXTERNAL',
      }
    );

  const [uploadInternalResources] = useUploadInternalProjectResourceMutation();
  const [deleteInternalProjectResource] =
    useDeleteInternalProjectResourceMutation();

  const [uploadExternalResources] = useUploadExternalProjectResourceMutation();
  const [deleteExternalProjectResource] = useDeleteExternalProjectResourceMutation();


  // Always use live API data so cache invalidation works correctly
  const docs = normalizeProjectDocs(projectType === 'EXTERNAL' ? project.resources : data);

  // Multi-file upload (one request per file)
  const uploadFiles = useCallback(
    async (files: FileList) => {
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formData = new FormData();

          formData.append('path', file); // actual file
          formData.append('name', file.name);
          formData.append('description', ''); // optional
          formData.append('project', String(project?.id));
          formData.append('type', file.type);

          if (projectType === 'EXTERNAL') {
            await uploadExternalResources({
              tenant: tenant!,
              data: formData,
            }).unwrap();
          } else {
            await uploadInternalResources({
              tenant: tenant!,
              data: formData,
            }).unwrap();
          }
        }
      } catch (error) {
        console.error('File upload error:', error);
      }
    },
    [tenant, uploadInternalResources, uploadExternalResources, project?.id]
  );

  // Trigger ConfirmDialog before deletion
  const handleDeleteClick = (doc: DocumentItem) => {
    setDocumentToDelete(doc);
    setConfirmDialogOpen(true);
  };

  // Confirm deletion
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    try {
      setLoading(true);
      if (projectType === 'EXTERNAL') {
        await deleteExternalProjectResource({
          tenant: tenant!,
          id: Number(documentToDelete.id),
        }).unwrap();
      } else {
        await deleteInternalProjectResource({
          tenant: tenant!,
          id: Number(documentToDelete.id),
        }).unwrap();
      }
      setConfirmDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-transparent border-none">
        {/* File upload button */}
        {canManage && <FilesUploadButton onFilesSelect={uploadFiles} multiple />}
        <WithLoadingErrorWrapper
          isFetching={isFetching}
          loading={isLoading}
          error={error as ApiErrorResponse}
          onRetry={refetch}
        >
          {docs.length > 0 ? (
            <DocumentGrid
              documents={docs}
              canEdit={canManage}
              onDelete={canManage ? handleDeleteClick : undefined}
            />
          ) : (
            <NoData
              title="No project resources"
              subTitle="This project doesn't have any resources yet."
            />
          )}
        </WithLoadingErrorWrapper>
      </Card>
      <ConfirmDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        loading={loading}
        config={
          documentToDelete
            ? {
              title: 'Delete Document',
              description: `Are you sure you want to delete "${documentToDelete.name}"?`,
              confirmText: 'Delete',
              cancelText: 'Cancel',
              destructive: true,
              onConfirm: handleConfirmDelete,
            }
            : null
        }
      />
    </>
  );
};

export default ProjectResources;
